import {useState, useEffect} from "react";
import {
  Box,
  Typography,
  IconButton,
  Divider,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Stack,
  TextField,
  Checkbox,
  FormControlLabel
} from "@mui/material";
import {Close, ExpandMore, Delete, Edit, Save, Cancel} from "@mui/icons-material";
import {toast} from "react-toastify";
import {GraphRepository} from "../../../../repositories/GraphRepository";
import useConfirm from "../../../../hooks/useConfirm";
import moment from "moment";

export const NodeInfoPanel = ({ node, onClose }) => {
  const [nodeDetails, setNodeDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editValues, setEditValues] = useState({});
  const confirmDelete = useConfirm(
    'Delete Node',
    'Are you sure you want to permanently delete this node? This action cannot be undone and will remove all relationships.'
  );
  const confirmToggle = useConfirm(
    'Toggle Active Status',
    'Are you sure you want to change the active status of this node?'
  );

  const fetchNodeDetails = () => {
    if (node) {
      setLoading(true);
      GraphRepository.getNodeById(node.group, node.id)
        .then((response) => {
          setNodeDetails(response.data);
          setLoading(false);
        })
        .catch((error) => {
          console.error("Error fetching node details:", error);
          setLoading(false);
        });
    }
  };

  useEffect(() => {
    if (node) {
      fetchNodeDetails();
    } else {
      setNodeDetails(null);
    }
  }, [node]);

  const handleToggleActive = async () => {
    const confirmed = await confirmToggle();
    if (!confirmed) return;

    const toastId = toast.loading("Toggling active status...");

    try {
      await GraphRepository.toggleActive(node.group, node.id);
      toast.update(toastId, {
        render: "Active status updated successfully!",
        type: "success",
        isLoading: false,
        autoClose: 3000
      });
      fetchNodeDetails();
    } catch (error) {
      toast.update(toastId, {
        render: error?.response?.data?.message || "Failed to toggle active status",
        type: "error",
        isLoading: false,
        autoClose: 5000
      });
      console.error("Error toggling active status:", error);
    }
  };

  const handleDelete = async () => {
    const confirmed = await confirmDelete();
    if (!confirmed) return;

    const toastId = toast.loading("Deleting node...");

    try {
      await GraphRepository.deleteNode(node.group, node.id);
      toast.update(toastId, {
        render: "Node deleted successfully!",
        type: "success",
        isLoading: false,
        autoClose: 3000
      });
      onClose();
    } catch (error) {
      toast.update(toastId, {
        render: error?.response?.data?.message || "Failed to delete node",
        type: "error",
        isLoading: false,
        autoClose: 5000
      });
      console.error("Error deleting node:", error);
    }
  };

  const handleEditChange = (key, value) => {
    setEditValues(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleCancel = () => {
    setIsEditMode(false);
    setEditValues({});
  };

  const handleSave = async () => {
    const toastId = toast.loading("Updating node...");

    try {
      // Convert values to proper types
      const properties = {};
      Object.entries(editValues).forEach(([key, value]) => {
        const originalValue = nodeDetails.node.properties[key];
        const fieldType = getFieldType(key, originalValue);

        if (fieldType === "boolean") {
          properties[key] = Boolean(value);
        } else if (fieldType === "integer") {
          properties[key] = parseInt(value, 10);
        } else if (fieldType === "float") {
          properties[key] = parseFloat(value);
        } else if (fieldType === "datetime") {
          properties[key] = value; // Keep as ISO string
        } else if (fieldType === "array" || fieldType === "object") {
          properties[key] = typeof value === "string" ? JSON.parse(value) : value;
        } else if (fieldType === "null") {
          properties[key] = null;
        } else {
          properties[key] = String(value);
        }
      });

      await GraphRepository.updateNode(node.group, node.id, properties);
      toast.update(toastId, {
        render: "Node updated successfully!",
        type: "success",
        isLoading: false,
        autoClose: 3000
      });
      setIsEditMode(false);
      fetchNodeDetails();
    } catch (error) {
      toast.update(toastId, {
        render: error?.response?.data?.message || "Failed to update node",
        type: "error",
        isLoading: false,
        autoClose: 5000
      });
      console.error("Error updating node:", error);
    }
  };

  const getFieldType = (key, value) => {
    // Check if we have property metadata from backend
    const propertyMeta = nodeDetails?.node?.property_objects?.find(p => p.key === key);
    if (propertyMeta) {
      return propertyMeta.type;
    }

    // Fallback to inferring type from value
    if (typeof value === "boolean") return "boolean";
    if (typeof value === "number") {
      return Number.isInteger(value) ? "integer" : "float";
    }
    if (Array.isArray(value)) return "array";
    if (typeof value === "object" && value !== null) return "object";
    if (value === null) return "null";
    // Check if it's a date/datetime string
    if (typeof value === "string" && moment(value, moment.ISO_8601, true).isValid()) {
      return "datetime";
    }
    return "string";
  };

  const convertNeo4jDateTime = (dateObj) => {
    if (dateObj && dateObj._DateTime__date && dateObj._DateTime__time) {
      const date = dateObj._DateTime__date;
      const time = dateObj._DateTime__time;

      // Construct ISO string from Neo4j datetime object
      const isoString = `${date._Date__year}-${String(date._Date__month).padStart(2, '0')}-${String(date._Date__day).padStart(2, '0')}T${String(time._Time__hour).padStart(2, '0')}:${String(time._Time__minute).padStart(2, '0')}:${String(time._Time__second).padStart(2, '0')}`;
      return isoString;
    }
    return dateObj;
  };


  const formatDateValue = (value) => {
    console.log("FORMAT DATE", value)
    const momentDate = moment(new Date(value));
    // Check if it has time component
    if (momentDate.format('HH:mm:ss') !== '00:00:00') {
      return momentDate.format('YYYY-MM-DD HH:mm:ss');
    }
    return momentDate.format('YYYY-MM-DD');
  };

  const renderEditableField = (key, value) => {
    const fieldType = getFieldType(key, value);

    if (fieldType === "boolean") {
      return (
        <FormControlLabel
          control={
            <Checkbox
              checked={editValues[key] || false}
              onChange={(e) => handleEditChange(key, e.target.checked)}
              size="small"
            />
          }
          label=""
        />
      );
    }

    if (fieldType === "integer" || fieldType === "float") {
      return (
        <TextField
          fullWidth
          size="small"
          type="number"
          value={editValues[key] ?? ""}
          onChange={(e) => handleEditChange(key, e.target.value)}
          inputProps={{
            step: fieldType === "float" ? "0.01" : "1"
          }}
        />
      );
    }

    if (fieldType === "datetime") {
      const hasTime = value && moment(value).format('HH:mm:ss') !== '00:00:00';
      return (
        <TextField
          fullWidth
          size="small"
          type={hasTime ? "datetime-local" : "date"}
          value={editValues[key] ?? ""}
          onChange={(e) => handleEditChange(key, e.target.value)}
          InputLabelProps={{
            shrink: true,
          }}
        />
      );
    }

    if (fieldType === "array" || fieldType === "object") {
      return (
        <TextField
          fullWidth
          size="small"
          value={typeof editValues[key] === "object" ? JSON.stringify(editValues[key]) : editValues[key] ?? ""}
          onChange={(e) => {
            try {
              const parsed = JSON.parse(e.target.value);
              handleEditChange(key, parsed);
            } catch {
              handleEditChange(key, e.target.value);
            }
          }}
          multiline
          rows={3}
          placeholder={fieldType === "array" ? "[]" : "{}"}
        />
      );
    }

    if (fieldType === "null") {
      return (
        <Typography variant="body2" color="text.secondary">
          null
        </Typography>
      );
    }

    return (
      <TextField
        fullWidth
        size="small"
        value={editValues[key] ?? ""}
        onChange={(e) => handleEditChange(key, e.target.value)}
        multiline={key === "description"}
        rows={key === "description" ? 3 : 1}
      />
    );
  };

  if (!node) return null;

  return (
    <Box
      className="border-shadow"
      sx={{
        position: "absolute",
        top: 10,
        right: 20,
        zIndex: 100,
        bgcolor: "background.paper",
        borderRadius: 3,
        minWidth: 300,
        maxWidth: 480,
        width: "90%",
        maxHeight: "calc(90dvh - 100px)",
        display: "flex",
        flexDirection: "column",
        "& > *": {fontFamily: "Roboto !important"}
      }}
    >
      {/* Fixed Header */}
      <Box sx={{ p: 2, pb: 1 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Node Information
          </Typography>
          <IconButton size="small" onClick={onClose}>
            <Close fontSize="small" />
          </IconButton>
        </Box>
        <Divider />
      </Box>

      {/* Scrollable Content */}
      <Box sx={{ flex: 1, overflowY: "auto", px: 2 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          <Box>
          <Box sx={{ mb: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Chip label={node.group} color="primary" variant="outlined" />
          </Box>

          {nodeDetails?.node?.property_objects && nodeDetails.node.property_objects.length > 0 ? (
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, boxShadow: 0, mb: 2 }}>
              <Table size="small">
                <TableBody>
                  {nodeDetails.node.property_objects.map((prop) => {
                    const { key, type, value } = prop;
                    const displayValue = type === "datetime" ? convertNeo4jDateTime(value) : value;

                    return (
                      <TableRow key={key}>
                        <TableCell
                          sx={{
                            fontWeight: 500,
                            textTransform: "capitalize",
                            width: "40%",
                            fontFamily: "Roboto !important"
                          }}
                        >
                          {key}
                        </TableCell>
                        <TableCell sx={{ wordBreak: "break-word", fontFamily: "Roboto !important", py: isEditMode ? 1 : undefined }}>
                          {isEditMode ? (
                            renderEditableField(key, displayValue)
                          ) : (
                            (() => {
                              if (type === "boolean") {
                                return value ? "Yes" : "No";
                              }
                              if (type === "datetime") {
                                return formatDateValue(displayValue);
                              }
                              if (type === "array" || type === "object") {
                                return JSON.stringify(value);
                              }
                              if (type === "null" || value === null) {
                                return "null";
                              }
                              return value?.toString() || "N/A";
                            })()
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : !loading && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              No properties available
            </Typography>
          )}

          {/* Outgoing Triples */}
          {nodeDetails?.outgoing_triples && nodeDetails.outgoing_triples.count > 0 && (
            <Accordion sx={{ mb: 1, boxShadow: 0 }}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Outgoing Relations ({nodeDetails.outgoing_triples.count})
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <Box>
                  {nodeDetails.outgoing_triples.triples.map((triple, index) => (
                    <Box
                      key={index}
                      sx={{
                        p: 1.5,
                        borderBottom: index < nodeDetails.outgoing_triples.triples.length - 1 ? 1 : 0,
                        borderColor: "divider"
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
                        {triple.rdf_notation}
                      </Typography>
                      <Box sx={{ mt: 1 }}>
                        <Chip
                          label={triple.predicate.type}
                          size="small"
                          color="secondary"
                          variant="outlined"
                          sx={{ mr: 1 }}
                        />
                        <Chip
                          label={triple.object.type}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                      <Typography variant="body2" sx={{ mt: 1, fontSize: "0.8rem" }}>
                        {triple.object.properties?.name || triple.object.properties?.searchName || `ID: ${triple.object.id}`}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Incoming Triples */}
          {nodeDetails?.incoming_triples && nodeDetails.incoming_triples.count > 0 && (
            <Accordion sx={{ boxShadow: 0 }}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Incoming Relations ({nodeDetails.incoming_triples.count})
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <Box>
                  {nodeDetails.incoming_triples.triples.map((triple, index) => (
                    <Box
                      key={index}
                      sx={{
                        p: 1.5,
                        borderBottom: index < nodeDetails.incoming_triples.triples.length - 1 ? 1 : 0,
                        borderColor: "divider"
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
                        {triple.rdf_notation}
                      </Typography>
                      <Box sx={{ mt: 1 }}>
                        <Chip
                          label={triple.subject.type}
                          size="small"
                          variant="outlined"
                          sx={{ mr: 1 }}
                        />
                        <Chip
                          label={triple.predicate.type}
                          size="small"
                          color="secondary"
                          variant="outlined"
                        />
                      </Box>
                      <Typography variant="body2" sx={{ mt: 1, fontSize: "0.8rem", fontFamily: "Roboto !important" }}>
                        {triple.subject.properties?.name || triple.subject.properties?.searchName || `ID: ${triple.subject.id}`}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>
          )}
        </Box>
      )}
      </Box>

      {/* Fixed Footer */}
      {!loading && nodeDetails && (
        <Stack direction={"row"} gap={2} justifyContent={"flex-end"} sx={{ p: 2, pt: 1, borderTop: 1, borderColor: "divider" }}>
          {isEditMode ? (
            <>
              <Button
                variant="outlined"
                sx={{borderRadius: 5}}
                startIcon={<Cancel />}
                onClick={handleCancel}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                sx={{borderRadius: 5}}
                startIcon={<Save />}
                onClick={handleSave}
              >
                Save
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outlined"
                color={"primary"}
                sx={{borderRadius: 5, px: 2, mr: "auto"}}
                onClick={() => {
                  setIsEditMode(true);
                  // Build editValues from property_objects
                  const values = {};
                  nodeDetails.node.property_objects.forEach(prop => {
                    if (prop.type === "datetime") {
                      values[prop.key] = convertNeo4jDateTime(prop.value);
                    } else {
                      values[prop.key] = prop.value;
                    }
                  });
                  setEditValues(values);
                }}
                startIcon={<Edit />}
              >
                Edit Properties
              </Button>
              <Button
                variant="outlined"
                color={"primary"}
                sx={{borderRadius: 5, px: 2}}
                onClick={handleToggleActive}
              >
                {nodeDetails?.node?.properties?.active ? "Active" : "Not Active"}
              </Button>
              <Button
                variant="contained"
                color="primary"
                sx={{borderRadius: 5}}
                startIcon={<Delete />}
                onClick={handleDelete}
              >
                Delete
              </Button>
            </>
          )}
        </Stack>
      )}
    </Box>
  );
};